/**
 * Detection, engagement, and memory: the part of a brain that is the same for
 * every role (PRD #793).
 *
 * Whether a monster has noticed you is not a property of how it fights, so it
 * lives here and every brain calls {@link rememberTarget} first. The rules it
 * implements are the ones the PRD's user stories ask for, in one place where
 * they can be read together:
 *
 * - **Detection is proximity plus sight.** A monster acquires a target that is
 *   inside {@link DETECTION_RADIUS_TILES} *and* visible, so a wall you cannot
 *   see past is a wall it cannot aggro through.
 * - **Damage always activates.** {@link activateMemory} is what an incoming hit
 *   calls, and it engages regardless of sight or distance, so opening on
 *   something that had not noticed you still starts a fight.
 * - **Memory survives a corner.** An engaged monster keeps its target and
 *   pursues {@link BrainMemory.lastKnown} while it cannot see it, so breaking
 *   line of sight is a moment of pursuit rather than an escape.
 * - **Memory expires, generously.** After {@link MEMORY_TICKS} with no
 *   perception at all the target is dropped and the monster goes idle. It keeps
 *   every point of damage it has taken: there is no leash, no reset, no
 *   invulnerability, and no return-home heal. Those are for bosses.
 *
 * Nothing here is a policy a role can override, and nothing here reads the
 * world: the caller supplies perception and this transforms memory.
 */

import type { EntityId } from '../../entity';
import type { BrainMemory, BrainPerception, BrainPoint, PerceivedTarget } from './types';

/**
 * How close a target has to be before an unengaged monster notices it, in
 * tiles.
 *
 * Twelve tiles is most of a room, so walking in is what starts the fight and a
 * player rarely gets to stand in the open picking off a pack that has not
 * noticed them. It is deliberately longer than the reach of anything a monster
 * carries: noticing you and being able to act on it are separate, and a
 * ranged monster that acquires you at twelve tiles still has to close to its
 * own band before it can shoot.
 */
export const DETECTION_RADIUS_TILES = 12;

/**
 * How long an engaged monster keeps a target it can no longer perceive, in 50ms
 * ticks.
 *
 * Ten seconds is deliberately generous: it is long enough that ducking behind a
 * pillar is a tactic rather than an exit, and short enough that a genuine
 * disengage across a level ends the fight.
 */
export const MEMORY_TICKS = 200;

function perceived(
  perception: BrainPerception,
  id: EntityId | null,
): PerceivedTarget | null {
  if (id === null) return null;
  return perception.targets.find((target) => target.id === id) ?? null;
}

/**
 * Whether this target is one an unengaged monster would notice: close enough
 * and in sight.
 */
export function isDetectable(target: PerceivedTarget): boolean {
  return target.visible && target.distanceTiles <= DETECTION_RADIUS_TILES;
}

/**
 * Advance memory for one tick of perception, returning a fresh record.
 *
 * Acquisition prefers the nearest detectable target, with entity id breaking a
 * tie, so target selection is deterministic without drawing from the rng. An
 * already-engaged target is kept while it can still be perceived at all, even
 * from beyond the detection radius: noticing you is harder than remembering
 * you.
 */
export function rememberTarget(
  memory: BrainMemory,
  perception: BrainPerception,
): BrainMemory {
  const engaged = perceived(perception, memory.target);
  if (engaged?.visible) {
    return {
      ...memory,
      target: engaged.id,
      lastKnown: { ...engaged.position },
      ticksSincePerceived: 0,
    };
  }
  if (memory.target !== null) {
    const ticksSincePerceived = memory.ticksSincePerceived + 1;
    // The target is gone from the world entirely, or it has been out of
    // perception for longer than memory lasts. Either way the fight is over,
    // and nothing about the monster is restored by it ending.
    if (!engaged || ticksSincePerceived > MEMORY_TICKS) {
      return {
        ...memory,
        target: null,
        lastKnown: null,
        ticksSincePerceived: 0,
        commitTicks: 0,
        commitPoint: null,
      };
    }
    return { ...memory, ticksSincePerceived };
  }
  const acquired = [...perception.targets]
    .filter(isDetectable)
    .sort((left, right) =>
      left.distanceTiles === right.distanceTiles
        ? left.id - right.id
        : left.distanceTiles - right.distanceTiles,
    )[0];
  if (!acquired) return memory;
  return {
    ...memory,
    target: acquired.id,
    lastKnown: { ...acquired.position },
    ticksSincePerceived: 0,
  };
}

/**
 * Engage a target because it just did something to this monster, sight and
 * distance notwithstanding.
 *
 * An already-engaged monster keeps the target it has rather than switching to
 * whoever hit it last, so a fight cannot be pulled apart by chip damage. There
 * are no parties yet, so in practice there is only ever one attacker.
 */
export function activateMemory(
  memory: BrainMemory,
  attacker: EntityId,
  at: BrainPoint,
): BrainMemory {
  if (memory.target !== null && memory.target !== attacker) return memory;
  return {
    ...memory,
    target: attacker,
    lastKnown: { x: at.x, y: at.y },
    ticksSincePerceived: 0,
  };
}

/** The target this brain is fighting, if it can currently perceive it. */
export function currentTarget(
  perception: BrainPerception,
  memory: BrainMemory,
): PerceivedTarget | null {
  return perceived(perception, memory.target);
}

/** A unit vector from `from` to `to`, or null when they coincide. */
export function directionBetween(
  from: BrainPoint,
  to: BrainPoint,
): BrainPoint | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length === 0) return null;
  return { x: dx / length, y: dy / length };
}

/** The point `distance` tiles from `from` along the direction to `toward`. */
export function pointToward(
  from: BrainPoint,
  toward: BrainPoint,
  distance: number,
): BrainPoint {
  const direction = directionBetween(from, toward) ?? { x: 0, y: 1 };
  return {
    x: from.x + direction.x * distance,
    y: from.y + direction.y * distance,
  };
}
